import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Link } from "react-router-dom";
import { Clock, MapPin, KeyRound, Copy, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import type { BookingDetail, AddonSelection } from "./types";
// UPPDRAG-36: clickable creator name → /manager/customers/:id.
import { PlayerNameButton } from "@/components/shared/PlayerNameButton";

interface BookingDetailInfoProps {
  booking: BookingDetail;
  bookerEmail: string | null;
  addonSelections: AddonSelection[];
  /** UPPDRAG-36: callback to close the surrounding modal/sheet on navigate. */
  onAfterNavigate?: () => void;
}

export function BookingDetailInfo({ booking, bookerEmail: _bookerEmail, addonSelections, onAfterNavigate }: BookingDetailInfoProps) {
  const { t } = useTranslation();
  const courtName = booking.courts?.name ?? "";
  const hallName = booking.courts?.halls?.name ?? "";
  const courtType = booking.courts?.court_type ?? "";

  const copyPin = () => {
    navigator.clipboard.writeText(booking.pin_code);
    toast.success(t("booking:toast.doorCodeCopied"));
  };

  return (
    <>
      {/* Date & Time */}
      <div className="rounded-lg bg-muted p-4 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium">
            {format(new Date(booking.start_time), "EEE d MMM", { locale: sv })} — {format(new Date(booking.start_time), "HH:mm")}–{format(new Date(booking.end_time), "HH:mm")}
          </span>
          <Badge variant="outline" className="text-[10px] ml-auto">{booking.duration_minutes} min</Badge>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>{courtName} — {hallName}</span>
          {courtType && <Badge variant="secondary" className="text-[10px]">{courtType}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>Dörrkod: <span className="font-mono font-bold text-lg">{booking.pin_code}</span></span>
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-1" onClick={copyPin}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
        {booking.is_open_match && !booking.activity_id && (
          <Badge className="bg-orange-500/10 text-orange-700 border-orange-300">Aktivitet</Badge>
        )}
      </div>

      {/* Activity link banner */}
      {booking.activity_id && (
        <Link
          to={`/activities/${booking.activity_id}`}
          className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/20 p-3 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
        >
          <span>Del av aktivitet</span>
          <ExternalLink className="h-3.5 w-3.5 ml-auto" />
        </Link>
      )}

      {/* Booked by — PR-AN: shows the actual CREATOR (admin/klubbkonto when
          admin booked åt kund, the player when they booked themselves).
          The "for whom" booking-attribution is shown elsewhere (Spelare-
          sektionen + customer-info). Email/phone follow booker_email
          (kunden) since "Bokad av" is now creator-only — contact info for
          the customer lives in the players list.
          UPPDRAG-36: creator name is now a PlayerNameButton — clicks
          navigate to /manager/customers/:id when the creator has a
          customer row in this org. Falls back to a static span when the
          creator is a staff admin without a customer profile. */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Bokad av</h4>
        <div className="text-sm space-y-0.5 font-medium">
          {(() => {
            const creatorName = booking.creator?.full_name
              || (booking as any).customers?.full_name
              || booking.profiles?.full_name
              || "Okänd";
            const creatorCustomerId = booking.creator?.customers?.[0]?.id
              ?? (booking as any).customers?.id
              ?? null;
            // No customer mapping → render plain span (no toast, less noise
            // for staff admins who never had a customer profile).
            if (!creatorCustomerId) {
              return <span>{creatorName}</span>;
            }
            return (
              <PlayerNameButton
                customerId={creatorCustomerId}
                name={creatorName}
                onAfterNavigate={onAfterNavigate}
              />
            );
          })()}
        </div>
      </div>

      <Separator />

      {/* Addons */}
      {addonSelections.length > 0 && (
        <div className="rounded-lg border p-3 space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tillägg</span>
          {addonSelections.map((sel) => {
            // Sprint D dual-state: prefer price_at_purchase_minor.
            const unitMoney = resolveMoney((sel as any).price_at_purchase_minor, sel.price_at_purchase, (sel as any).currency);
            const totalMinor = (unitMoney?.valueMinor ?? 0) * sel.quantity;
            return (
              <div key={sel.id} className="flex justify-between text-sm">
                <span>{sel.addon?.name ?? "Tillägg"} ×{sel.quantity}</span>
                <span className="font-medium">{formatPrice({ valueMinor: totalMinor, currency: unitMoney?.currency ?? "SEK" })}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Info text if exists */}
      {booking.info_text && (
        <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3">
          <p className="text-sm text-blue-900 dark:text-blue-100">{booking.info_text}</p>
        </div>
      )}
    </>
  );
}
